import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  teamsService,
  type TeamFilters,
  type AdminUpdateTeamRequest,
} from "@/api/services/teams.service";

export const teamKeys = {
  all: ["teams"] as const,
  lists: () => [...teamKeys.all, "list"] as const,
  list: (filters?: TeamFilters) => [...teamKeys.lists(), filters] as const,
  detail: (id: number) => [...teamKeys.all, "detail", id] as const,
};

export const useTeams = (filters?: TeamFilters) => {
  return useQuery({
    queryKey: teamKeys.list(filters),
    queryFn: () => teamsService.getAllTeams(filters),
    refetchInterval: 45000,
  });
};

export const useTeamDetail = (id: number) => {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => teamsService.getTeamById(id),
    enabled: !!id,
  });
};

export const useUpdateTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdminUpdateTeamRequest }) =>
      teamsService.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
};

export const useDeleteTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => teamsService.deleteTeam(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.lists() });
    },
  });
};
